import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Fade
} from '@mui/material';
import { LockOutlined, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../Apis/apiClient';
import { changePassword } from '../Apis/AuthApis';

const ForcePasswordChange = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Check if user needs to change password
    const savedUser = sessionStorage.getItem('iti_company_user');
    if (!savedUser) {
      navigate('/login');
      return;
    }

    // Parse user data and check password status
    try {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser.isPasswordChanged) {
        navigate('/dashboard'); // User already changed password, redirect to dashboard
      }
    } catch (error) {
      console.error('Failed to parse user data:', error);
      navigate('/login');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.currentPassword) {
      setError('Current password is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response?.success) {
        setSuccess('Password changed successfully! Redirecting to dashboard...');
        
        // Update user data in sessionStorage to reflect password change
        const savedUser = sessionStorage.getItem('iti_company_user');
        if (savedUser) {
          const parsedUser = JSON.parse(savedUser);
          parsedUser.isPasswordChanged = true;
          sessionStorage.setItem('iti_company_user', JSON.stringify(parsedUser));
        }
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response?.message || 'Failed to change password');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={500}>
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 2
                }}
              >
                <LockOutlined fontSize="large" />
              </Avatar>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Security Update Required
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 300, mx: 'auto' }}>
                For your security, please create a new password to continue using your account.
              </Typography>
            </Box>

            {/* Alert Messages */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: 2 }}
                icon={<CheckCircleOutline />}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ mb: 3, borderRadius: 2 }}
              >
                {success}
              </Alert>
            )}

            {/* Form Section */}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                margin="normal"
                required
                variant="outlined"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <LockOutlined sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <TextField
                fullWidth
                type="password"
                label="New Password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                margin="normal"
                required
                variant="outlined"
                sx={{ mb: 2 }}
                helperText="Must be at least 6 characters long"
                InputProps={{
                  startAdornment: <LockOutlined sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                margin="normal"
                required
                variant="outlined"
                sx={{ mb: 3 }}
                helperText="Re-enter your new password"
                InputProps={{
                  startAdornment: <LockOutlined sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Update Password'
                )}
              </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Need help? Contact your system administrator
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForcePasswordChange;
